<script lang="ts">
  /**
   * Home view: shows app tiles with search and category filter pills.
   * Pseudocode:
   * - Load apps from registry
   * - Filter by text and category
   */
  import { apps } from '$apps/registry';
  import { prefs, isHidden, isPinned, togglePin, toggleHidden, setOrder, resetPrefs, setCategory, addCategory, renameCategory } from '$lib/state/prefs';
  import HeroBanner from '$lib/components/HeroBanner.svelte';
  import ToolCard from '$lib/components/ToolCard.svelte';

  let q = '';
  let activeFilter = 'All';
  let customizing = false;
  let showHidden = false;
  let showManager = false;

  // Category definitions for filter pills
  const CATEGORIES = [
    { id: 'All', label: 'All' },
    { id: 'PDF', label: 'PDF Tools' },
    { id: 'Images', label: 'Images' },
    { id: 'Audio/Video', label: 'Audio/Video' },
    { id: 'Data', label: 'Data' },
    { id: 'Text', label: 'Text' },
    { id: 'Privacy', label: 'Privacy' },
    { id: 'Games', label: 'Games' },
    { id: 'Productivity', label: 'Productivity' },
    { id: 'Other', label: 'Other' },
  ];

  // Compute display order: pins first (preserving order), then the rest, based on saved order
  $: prefVal = $prefs;
  $: baseOrder = (prefVal.order.length ? prefVal.order : apps.map(a => a.id));
  $: byId = new Map(apps.map(a => [a.id, a] as const));
  $: orderedApps = baseOrder.map(id => byId.get(id)).filter(Boolean);
  // Append any new apps not seen before
  $: orderedApps = [
    ...orderedApps as typeof apps,
    ...apps.filter(a => !baseOrder.includes(a.id))
  ];

  // Category grouping
  function defaultCategory(a: any): string {
    const t = (a.tags || []) as string[];
    if (t.includes('pdf')) return 'PDF';
    if (t.includes('images')) return 'Images';
    if (t.includes('audio') || t.includes('video')) return 'Audio/Video';
    if (t.includes('csv') || t.includes('json') || t.includes('yaml')) return 'Data';
    if (t.includes('text')) return 'Text';
    if (t.includes('privacy')) return 'Privacy';
    if (t.includes('qr')) return 'QR';
    if (t.includes('sharing') || t.includes('contacts')) return 'Sharing';
    if (t.includes('games') || t.includes('board')) return 'Games';
    if (t.includes('docs') || t.includes('health')) return 'Docs & Forms';
    if (t.includes('productivity') || t.includes('time') || t.includes('scheduling') || t.includes('utility')) return 'Productivity';
    return 'Other';
  }

  $: appCategory = (id: string) => prefVal.categories[id] || defaultCategory(byId.get(id));

  // Apply filters: search, category, and hidden
  $: filteredApps = orderedApps.filter(a => {
    if (!a) return false;
    // Search filter
    if (q && !(a.name.toLowerCase().includes(q.toLowerCase()) || a.tags?.some(t => t.includes(q.toLowerCase())))) return false;
    // Category filter
    if (activeFilter !== 'All' && appCategory(a.id) !== activeFilter) return false;
    // Hidden filter
    if (!customizing && isHidden(prefVal, a.id)) return false;
    if (customizing && !showHidden && isHidden(prefVal, a.id)) return false;
    return true;
  });

  // Split into pinned and others
  $: pinnedApps = filteredApps.filter(a => isPinned(prefVal, a.id));
  $: otherApps = filteredApps.filter(a => !isPinned(prefVal, a.id));

  function changeCategory(id: string, current: string) {
    let name = prompt('Move to category:', current) || current;
    name = name.trim() || current;
    setCategory(id, name);
    addCategory(name);
  }

  function addNewCategory() {
    const name = (prompt('New category name:') || '').trim();
    if (name) addCategory(name);
  }

  function renameCat(cat: string) {
    const name = (prompt('Rename category:', cat) || cat).trim();
    if (name && name !== cat) renameCategory(cat, name);
  }

  // Drag-and-drop reorder handling
  let dragId: string | null = null;
  function onDragStart(id: string, ev: DragEvent) {
    dragId = id; ev.dataTransfer?.setData('text/plain', id);
  }
  function onDragOver(ev: DragEvent) { if (customizing) ev.preventDefault(); }
  function onDrop(targetId: string, ev: DragEvent) {
    if (!customizing) return;
    ev.preventDefault();
    const fromId = dragId || ev.dataTransfer?.getData('text/plain');
    if (!fromId || fromId === targetId) return;
    const order = baseOrder.slice();
    const fromIdx = order.indexOf(fromId);
    const toIdx = order.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    order.splice(toIdx, 0, ...order.splice(fromIdx, 1));
    setOrder(order);
    dragId = null;
  }
</script>

<!-- Hero -->
<HeroBanner />

<!-- Category Filter Pills + Search -->
<div class="home-container">
  <div class="filter-row">
    <div class="filter-pills">
      {#each CATEGORIES as cat}
        <button
          class="filter-pill"
          class:active={activeFilter === cat.id}
          on:click={() => activeFilter = cat.id}
        >
          {cat.label}
        </button>
      {/each}
    </div>
  </div>

  <div class="search-row">
    <div class="search-wrap">
      <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input
        class="search-input"
        placeholder="Search tools…"
        bind:value={q}
      />
    </div>

    <div class="controls">
      <button class="ctrl-btn" on:click={() => customizing = !customizing} class:active={customizing}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
        {customizing ? 'Done' : 'Customize'}
      </button>
    </div>
  </div>

  {#if customizing}
    <div class="customize-bar">
      <button class="cbar-btn" on:click={addNewCategory}>+ Category</button>
      <button class="cbar-btn" on:click={() => showManager = !showManager}>{showManager ? 'Close Manager' : 'Manage'}</button>
      <label class="cbar-btn cbar-check">
        <input type="checkbox" bind:checked={showHidden} /> Show hidden
      </label>
      <button class="cbar-btn cbar-reset" on:click={resetPrefs}>Reset</button>
    </div>
  {/if}

  {#if customizing && showManager}
    <div class="manager-panel">
      <div class="manager-title">Category Manager</div>
      {#each CATEGORIES.filter(c => c.id !== 'All') as cat}
        <div class="manager-row">
          <span class="manager-cat-name">{cat.label}</span>
          <div class="manager-actions">
            <button class="manager-btn" on:click={() => renameCat(cat.id)}>Rename</button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  <!-- Tool Grid -->
  <div class="tool-grid-wrapper">
  <div class="tool-grid">
    {#each [...pinnedApps, ...otherApps] as a, i (a.id)}
      <div
        role="listitem"
        draggable={customizing}
        on:dragstart={(ev) => onDragStart(a.id, ev)}
        on:dragover={onDragOver}
        on:drop={(ev) => onDrop(a.id, ev)}
      >
        <ToolCard
          app={a}
          category={appCategory(a.id)}
          index={i}
          {customizing}
          isPinned={isPinned(prefVal, a.id)}
          isHidden={isHidden(prefVal, a.id)}
          onTogglePin={() => togglePin(a.id)}
          onToggleHidden={() => toggleHidden(a.id)}
          onChangeCategory={() => changeCategory(a.id, appCategory(a.id))}
        />
      </div>
    {/each}
  </div>
  </div>

  {#if filteredApps.length === 0}
    <div class="empty-state">
      <p>No tools found matching your search.</p>
    </div>
  {/if}
</div>

<style>
  .home-container {
    max-width: 76rem;
    margin: 0 auto;
    padding: 0 1.5rem 5rem;
  }

  /* ==================== FILTER PILLS ==================== */
  .filter-row {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.75rem;
  }
  .filter-pills {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0.25rem;
    border-radius: 9999px;
    background: hsl(var(--theme-muted) / 0.06);
    border: 1px solid hsl(var(--theme-border) / 0.4);
  }
  .filter-pill {
    padding: 0.4375rem 0.875rem;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: hsl(var(--theme-muted-foreground));
    background: transparent;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 250ms var(--theme-ease);
    font-family: 'Inter', sans-serif;
    white-space: nowrap;
  }
  .filter-pill:hover {
    color: hsl(var(--theme-foreground));
    background: hsl(var(--theme-muted) / 0.12);
  }
  .filter-pill.active {
    color: hsl(var(--theme-primary-foreground));
    background: hsl(var(--theme-primary));
    border-color: hsl(var(--theme-primary));
    box-shadow: 0 1px 4px hsl(var(--theme-primary) / 0.25);
    font-weight: 600;
  }

  /* ==================== SEARCH ==================== */
  .search-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
  }
  .search-wrap {
    position: relative;
    flex: 1;
    min-width: 280px;
  }
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: hsl(var(--theme-muted-foreground) / 0.6);
    pointer-events: none;
    transition: color 200ms ease;
  }
  .search-wrap:focus-within .search-icon {
    color: hsl(var(--theme-primary));
  }
  .search-input {
    width: 100%;
    height: 2.75rem;
    padding: 0 1rem 0 2.75rem;
    border-radius: var(--theme-radius-lg);
    border: 1.5px solid hsl(var(--theme-border) / 0.5);
    background: hsl(var(--theme-card) / 0.5);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
    color: hsl(var(--theme-foreground));
    transition: all 250ms var(--theme-ease);
    outline: none;
  }
  .search-input::placeholder { color: hsl(var(--theme-muted-foreground) / 0.6); }
  .search-input:focus {
    border-color: hsl(var(--theme-primary) / 0.5);
    background: hsl(var(--theme-card));
    box-shadow: 0 0 0 3px hsl(var(--theme-primary) / 0.08), var(--theme-shadow-sm);
  }

  /* Controls */
  .controls { display: flex; gap: 0.5rem; }
  .ctrl-btn {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.875rem;
    border-radius: var(--theme-radius);
    border: 1px solid hsl(var(--theme-border) / 0.5);
    background: hsl(var(--theme-card) / 0.5);
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 200ms ease;
    font-family: 'Inter', sans-serif;
  }
  .ctrl-btn:hover, .ctrl-btn.active {
    color: hsl(var(--theme-foreground));
    background: hsl(var(--theme-muted) / 0.15);
    border-color: hsl(var(--theme-primary) / 0.3);
  }

  /* Customize bar */
  .customize-bar {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
    padding: 0.75rem 1rem;
    border-radius: var(--theme-radius-lg);
    background: hsl(var(--theme-card));
    border: 1px solid hsl(var(--theme-border) / 0.5);
  }
  .cbar-btn {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.35rem 0.7rem;
    border-radius: var(--theme-radius-sm);
    border: 1px solid hsl(var(--theme-border));
    background: transparent;
    color: hsl(var(--theme-muted-foreground));
    cursor: pointer;
    transition: all 200ms ease;
    font-family: 'Inter', sans-serif;
  }
  .cbar-btn:hover { color: hsl(var(--theme-foreground)); background: hsl(var(--theme-muted) / 0.2); }
  .cbar-check { display: flex; align-items: center; gap: 0.35rem; }
  .cbar-reset { color: hsl(var(--theme-destructive)); border-color: hsl(var(--theme-destructive) / 0.3); }

  /* Manager panel */
  .manager-panel {
    padding: 1.25rem;
    border-radius: var(--theme-radius-lg);
    background: hsl(var(--theme-card));
    border: 1px solid hsl(var(--theme-border) / 0.5);
    margin-bottom: 2rem;
  }
  .manager-title { font-weight: 600; font-size: 0.875rem; margin-bottom: 0.75rem; color: hsl(var(--theme-foreground)); }
  .manager-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid hsl(var(--theme-border) / 0.2);
  }
  .manager-row:last-child { border-bottom: none; }
  .manager-cat-name { font-size: 0.8125rem; color: hsl(var(--theme-foreground)); }
  .manager-actions { display: flex; gap: 0.375rem; }
  .manager-btn {
    font-size: 0.6875rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    border: 1px solid hsl(var(--theme-border));
    background: transparent;
    color: hsl(var(--theme-muted-foreground));
    cursor: pointer;
    transition: all 150ms ease;
    font-family: 'Inter', sans-serif;
  }
  .manager-btn:hover { background: hsl(var(--theme-muted) / 0.3); color: hsl(var(--theme-foreground)); }

  /* ==================== GRID ==================== */
  .tool-grid-wrapper {
    padding: 0;
  }
  .tool-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1rem;
  }

  /* Empty state */
  .empty-state {
    text-align: center;
    padding: 4rem 1rem;
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.9375rem;
  }

  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 1024px) {
    .tool-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
  }
  @media (max-width: 768px) {
    .home-container { padding: 0 1rem 3rem; }
    .filter-pills { gap: 0.25rem; padding: 0.1875rem; }
    .filter-pill { padding: 0.375rem 0.75rem; font-size: 0.75rem; }
    .tool-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0.75rem; }
  }
  @media (max-width: 540px) {
    .tool-grid { grid-template-columns: repeat(2, 1fr); gap: 0.625rem; }
    .search-row { flex-direction: column; }
    .search-wrap { min-width: 100%; }
    .filter-pills {
      border: none;
      background: none;
      padding: 0;
    }
    .filter-pill {
      border: 1px solid hsl(var(--theme-border) / 0.4);
    }
  }
  @media (max-width: 380px) {
    .tool-grid { grid-template-columns: 1fr; }
  }
</style>
